import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, KeyRound, Loader2, Mail, MailCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, Navigate } from 'react-router'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { apiErrorMessage } from '@/i18n'
import { authApi } from '@/lib/auth/api'
import { useAuth } from '@/lib/auth/AuthProvider'
import { AuthHeading, FieldError, FormAlert, IconInput } from './components/AuthFields'
import { AuthShell } from './components/AuthShell'

const iconBadge = 'flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-inset ring-primary/15'

export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const { status } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const form = useForm({
    resolver: zodResolver(z.object({ email: z.email(t('auth.invalidEmail')) })),
  })

  if (status === 'authenticated') return <Navigate to="/" replace />

  return (
    <AuthShell>
      {sent ? (
        <div className="space-y-5">
          <AuthHeading
            eyebrow={<span className={iconBadge}><MailCheck className="size-6" aria-hidden /></span>}
            title={t('auth.forgotSentTitle')}
            subtitle={t('auth.forgotSentBody')}
          />
          <Button asChild className="h-11 w-full rounded-xl">
            <Link to="/reset-password">{t('auth.resetContinue')}</Link>
          </Button>
          <Button asChild variant="ghost" className="w-full">
            <Link to="/login">
              <ArrowLeft />
              {t('auth.useOther')}
            </Link>
          </Button>
        </div>
      ) : (
        <form
          className="space-y-5"
          onSubmit={form.handleSubmit(async (v) => {
            setError(null)
            try {
              await authApi.forgotPassword(v.email)
              setSent(true)
            } catch (e) {
              setError(apiErrorMessage(e))
            }
          })}
        >
          <AuthHeading
            eyebrow={<span className={iconBadge}><KeyRound className="size-6" aria-hidden /></span>}
            title={t('auth.forgotTitle')}
            subtitle={t('auth.forgotSubtitle')}
          />
          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <IconInput id="email" icon={Mail} type="email" autoComplete="username" autoFocus placeholder={t('auth.emailPlaceholder')} {...form.register('email')} />
            <FieldError message={form.formState.errors.email?.message} />
          </div>
          {error ? <FormAlert>{error}</FormAlert> : null}
          <Button type="submit" className="h-11 w-full rounded-xl" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? <Loader2 className="animate-spin" /> : <Mail />}
            {t('auth.forgotSubmit')}
          </Button>
          <Button asChild type="button" variant="ghost" className="w-full">
            <Link to="/login">
              <ArrowLeft />
              {t('auth.useOther')}
            </Link>
          </Button>
        </form>
      )}
    </AuthShell>
  )
}
